import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Input component with WCAG accessibility improvements:
 * - aria-invalid when error is present
 * - aria-describedby linking to error message
 * - aria-required for required fields
 */
const Input = React.forwardRef(
  (
    {
      className,
      type,
      error,
      required,
      "aria-describedby": ariaDescribedBy,
      ...props
    },
    ref,
  ) => {
    // Generate unique ID for error message if error exists
    const errorId = error ? `${props.id || props.name}-error` : undefined;

    // Combine any existing aria-describedby with error ID
    const describedBy =
      [ariaDescribedBy, errorId].filter(Boolean).join(" ") || undefined;

    return (
      <input
        type={type}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          error && "border-destructive focus-visible:ring-destructive",
          className,
        )}
        ref={ref}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy}
        aria-required={required ? "true" : undefined}
        required={required}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

/**
 * InputField - Input with label and error message wrapper
 * Properly associates label, input, and error message for accessibility
 */
const InputField = React.forwardRef(
  ({ label, error, className, id, required, helpText, ...props }, ref) => {
    // Always call useId unconditionally, then use provided id if available
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;
    const helpId = helpText ? `${inputId}-help` : undefined;

    // Build describedby from help text and error
    const describedBy =
      [helpId, error ? errorId : undefined].filter(Boolean).join(" ") ||
      undefined;

    return (
      <div className={cn("space-y-2", className)}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {label}
            {required && (
              <span className="text-destructive ml-1" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}

        {helpText && (
          <p id={helpId} className="text-sm text-muted-foreground">
            {helpText}
          </p>
        )}

        <Input
          id={inputId}
          error={error}
          ref={ref}
          required={required}
          aria-describedby={describedBy}
          {...props}
        />

        {error && (
          <p id={errorId} className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  },
);
InputField.displayName = "InputField";

export { Input, InputField };
